import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MessageCircle, MapPin } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { externalLinks, CONTACT_EMAIL } from "../mock";

const inputCls =
  "bg-[#0f0f11] border-white/15 text-white placeholder:text-zinc-500 focus-visible:ring-[#ff2e4d]";

export default function Contact() {
  const [reason, setReason] = useState("General");
  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const name = fd.get("name") || "";
    const email = fd.get("email") || "";
    const phone = fd.get("phone") || "";
    const message = fd.get("message") || "";
    const subject = encodeURIComponent(`Bulls on Parade Darts — ${reason}`);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${encodeURIComponent(
      message ? `Reason: ${reason}\nName: ${name}\nEmail: ${email}\nPhone: ${phone}\n\n${message}` : ""
    )}`;
    toast.success("Opening your email…", {
      description: `Your message to ${CONTACT_EMAIL} is ready to send.`,
    });
    e.target.reset();
    setReason("General");
  };

  return (
    <main className="bop-vignette min-h-[70vh]">
      <section className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:px-8 py-16 lg:grid-cols-2 lg:py-20">
        <div>
          <p className="eyebrow">Get in touch</p>
          <h1 className="mt-4 font-display text-4xl font-extrabold text-white sm:text-5xl">Let’s talk darts.</h1>
          <p className="mt-5 max-w-xl text-lg text-zinc-400">
            Ordering supplies, booking coaching, sponsoring the program, or just have a question?
            Drop us a line and we’ll get back to you.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#ff2e4d]/15 text-[#ff2e4d]"><Mail className="h-5 w-5" /></span>
              <div>
                <div className="font-medium text-white">Email us</div>
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-[#ff2e4d] hover:underline break-all">{CONTACT_EMAIL}</a>
              </div>
            </div>
            <a href={externalLinks.discord} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#ff2e4d]/15 text-[#ff2e4d]"><MessageCircle className="h-5 w-5" /></span>
              <div>
                <div className="font-medium text-white group-hover:text-[#ff2e4d] transition-colors">Community Chat</div>
                <div className="text-sm text-zinc-400">Talk darts with players any hour</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#ff2e4d]/15 text-[#ff2e4d]"><MapPin className="h-5 w-5" /></span>
              <div>
                <div className="font-medium text-white">Based in</div>
                <div className="text-sm text-zinc-400">Ontario, Canada</div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0f0f11] p-6 sm:p-8">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Reason for contact</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General enquiry</SelectItem>
                  <SelectItem value="Supply Order">Supply order</SelectItem>
                  <SelectItem value="Used Darts">Used darts</SelectItem>
                  <SelectItem value="Coaching">Coaching</SelectItem>
                  <SelectItem value="Sponsorship">Sponsorship</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Full name <span className="text-[#ff2e4d]">*</span></Label>
              <Input name="name" required className={inputCls} placeholder="Your name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Email <span className="text-[#ff2e4d]">*</span></Label>
              <Input name="email" required type="email" className={inputCls} placeholder="you@email.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Phone</Label>
              <Input name="phone" className={inputCls} placeholder="(000) 000-0000" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Message <span className="text-[#ff2e4d]">*</span></Label>
              <Textarea name="message" required rows={5} className={inputCls} placeholder="How can we help?" />
            </div>
            <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors">
              Send message <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
